import {
  SCORE_BAND_CONFIG,
  SCORE_BAND_ORDER,
  ScoreBand,
} from "~/pages/manifold-nextgen/components/evals/constants/scoring";
import type { PartialEvalCaseResult, PartialEvalRun } from "~/services/evals";
import { isNumber, mean } from "./math";

/**
 * What every eval figure is derived from: one result's score, one run's clock,
 * one timestamp. Two durations live here and they are not interchangeable —
 * `resultDurationSeconds` is one case's own time, `runDurationSeconds` a run's
 * wall clock, which is not the sum of its cases' (they run concurrently).
 */
/** A run's results keyed by run id, as the matrix and trend both consume them. */
export type ResultsByRun = Record<string, PartialEvalCaseResult[]>;

/**
 * The rubric level a mean score rounds to. Bands are walked best-first, so a
 * score on a boundary takes the better band — 3.5 is "correct".
 */
export function scoreBandFor(score: number | null): ScoreBand | null {
  if (score == null) {
    return null;
  }
  return (
    SCORE_BAND_ORDER.find((band) => score >= SCORE_BAND_CONFIG[band].lowerBound) ??
    ScoreBand.INCORRECT
  );
}
/**
 * The duration one result took end to end, preferring `total_duration_seconds`
 * (turn plus judging) to `task_duration_seconds` (the turn alone). An unmeasured
 * result is not a fast one, so callers sort a null last rather than as zero.
 */
export function resultDurationSeconds(
  result: Pick<PartialEvalCaseResult, "metrics">
): number | null {
  const metrics = result.metrics;
  if (!metrics) {
    return null;
  }
  return metrics.total_duration_seconds ?? metrics.task_duration_seconds ?? null;
}
/**
 * One mean score per distinct case. The results list is case x iteration, so
 * averaging it directly weights a case by how many iterations were spent on it.
 */
export function caseMeanScores(results: PartialEvalCaseResult[]): number[] {
  const byCase = new Map<string, number[]>();
  for (const result of results) {
    if (result.score == null) {
      continue;
    }
    const scores = byCase.get(result.case_id);
    if (scores) {
      scores.push(result.score);
    } else {
      byCase.set(result.case_id, [result.score]);
    }
  }
  return [...byCase.values()].map((scores) => mean(scores)).filter(isNumber);
}
/**
 * The launch source recorded on a run, as `{trigger}:{user}`. The trigger is a
 * closed, colon-free set, so it is the first segment and the email is the rest.
 */
export function parseTriggeredBy(triggeredBy: string | null): {
  trigger: string | null;
  user: string | null;
} {
  if (!triggeredBy) {
    return { trigger: null, user: null };
  }
  const separator = triggeredBy.indexOf(":");
  if (separator === -1) {
    return { trigger: null, user: triggeredBy };
  }
  return {
    trigger: triggeredBy.slice(0, separator),
    user: triggeredBy.slice(separator + 1) || null,
  };
}
/**
 * Milliseconds for an API timestamp, or NaN when it is unparseable. The
 * conversations API can emit a doubly-terminated UTC offset (`+00:00Z`), which
 * `Date.parse` rejects outright, so the stray trailing `Z` is dropped.
 */
export function parseApiTimestamp(value: string | null | undefined): number {
  if (!value) {
    return NaN;
  }
  return Date.parse(value.replace(/([+-]\d{2}:?\d{2})Z$/, "$1"));
}
/**
 * A run's wall-clock duration in seconds, covering a run still going and one
 * whose timestamps do not describe an elapsed time. Nothing constrains
 * `completed_at` to follow `started_at`, and one stale value put a median run
 * time of -3574237.6s on the KPI row, so the pair is reported as unknown.
 */
export function runDurationSeconds(run: PartialEvalRun): number | null {
  if (!run.completed_at) {
    return null;
  }
  const started = parseApiTimestamp(run.started_at);
  const completed = parseApiTimestamp(run.completed_at);
  if (Number.isNaN(started) || Number.isNaN(completed) || completed < started) {
    return null;
  }
  return (completed - started) / 1000;
}
